<script setup lang="ts">
/** Progress toward the next price drop: "20 people involved — next drop at 30". */
const props = withDefaults(
  defineProps<{ participants: number; nextDropAt: number | null; showLabel?: boolean; compact?: boolean }>(),
  { showLabel: false, compact: false },
)

const pct = computed(() => {
  if (!props.nextDropAt) return 100
  return Math.min(100, Math.round((props.participants / props.nextDropAt) * 100))
})
</script>

<template>
  <div class="participants" :class="{ 'participants--compact': compact }">
    <p v-if="showLabel" class="participants__label">{{ participants }} people involved in this deal</p>
    <div
      class="participants__track"
      role="progressbar"
      :aria-valuenow="participants"
      aria-valuemin="0"
      :aria-valuemax="nextDropAt ?? participants"
      :aria-label="nextDropAt ? `${participants} of ${nextDropAt} people needed for the next price drop` : `${participants} people involved`"
    >
      <span class="participants__fill" :style="{ width: `${pct}%` }" />
      <span class="participants__thumb" :style="{ left: `${pct}%` }" />
    </div>
  </div>
</template>

<style scoped>
.participants__label {
  margin-bottom: 6px;
  font-size: 10px;
  line-height: 14px;
  color: var(--brand-700);
}

.participants__track {
  position: relative;
  height: 6px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--brand-900);
  background: repeating-linear-gradient(
    90deg,
    var(--gray-100) 0 calc(10% - 1px),
    var(--gray-300) calc(10% - 1px) 10%
  );
}

.participants__fill {
  position: absolute;
  inset: 0 auto 0 0;
  border-radius: var(--radius-pill);
  background: var(--brand-300);
}

.participants__thumb {
  position: absolute;
  top: 50%;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid var(--gray-600);
  background: var(--gray-100);
  transform: translate(-50%, -50%);
}

.participants--compact .participants__track {
  height: 4px;
}

.participants--compact .participants__thumb {
  width: 10px;
  height: 10px;
}
</style>
